<template>
  <div class="tm-paging-paginator">
    <tm-button
      icon-only
      size="xMedium"
      :disabled="isFirstPage || loading"
      @click="$emit('prev')"
    >
      <tm-icon name="tmi-chevron-left" />
    </tm-button>

    <tm-button
      icon-only
      size="xMedium"
      :disabled="isLastPage || loading"
      @click="$emit('next')"
    >
      <tm-icon name="tmi-chevron-right" />
    </tm-button>

    <div class="tm-paging-paginator__text">
      <slot
        name="pagination-text"
        :entity="entityName"
        :first-item-index="firstItemIndex"
        :last-item-index="lastIndex"
        :items-total="total"
        :items-total-string="itemsTotalString"
      >
        <tm-paging-paginator-data
          :entity="entityName"
          :first-index="firstItemIndex"
          :last-index="lastIndex"
          :total="total"
          :total-formatted="itemsTotalString"
        />
      </slot>

      <slot name="pagination-text-append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from '@/composition/vue/compositionApi'
import TmButton from '@/components/shared/TmButton.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import { getNumericService } from '@/core/container/ioc'
import { cursorLoadingText } from '@/services/tables/pagination/types'
import TmPagingPaginatorData from '@/components/shared/paging/TmPagingPaginatorData.vue'
import type { Nullable } from '@/types'

const props = defineProps<{
  entityName: string
  firstIndex: number
  lastIndex: number
  total?: number
  totalLimit?: Nullable<number>
  isFirstPage?: boolean
  isLastPage?: boolean
  loading?: boolean
}>()

defineEmits<{
  prev: []
  next: []
}>()

const numericService = getNumericService()

const totalToString = computed<string>(() => {
  return props.total && props.totalLimit && props.total >= props.totalLimit
    ? `${numericService.formatInteger(props.total)}+`
    : numericService.formatInteger(props.total || 0)
})

const itemsTotalString = computed<string>(() => {
  return props.loading && props.total === undefined ? cursorLoadingText : totalToString.value
})

const firstItemIndex = computed<number>(() => (props.total === 0 ? 0 : props.firstIndex + 1))
</script>

<style lang="scss" scoped>
.tm-paging-paginator {
  display: flex;
  align-items: center;

  &__text {
    margin-left: 10px;
    font-size: $sm-x-font;
    color: $gray8;
  }
}
</style>
